import { useReducer, useState } from 'react';
import { ServerError, ErrorType, State, Action } from './types';
import getCsrfToken from '@99designs/frontend-components/utils/app/getCsrfToken';
import Bugsnag from '@bugsnag/js';

export function calculateErrorType(error?: ServerError): ErrorType | null {
    if (!error) {
        return null;
    }

    if (error.type === 'Empty') {
        return 'TOKEN_EMPTY';
    }
    if (error.type === 'Expired') {
        return 'TOKEN_EXPIRED';
    }
    if (error.type === 'Invalid') {
        return 'TOKEN_INVALID';
    }
    if (error.type === 'Ratelimit') {
        return 'TOKEN_ATTEMPTS';
    }

    return null;
}

export function useTwoFactorUI(onAuthSuccess?: (target: string) => void) {
    const [state, dispatch] = useReducer(uiReducer, { error: null, ui: 'IDLE' });
    const [resendRequestCount, setResendRequestCount] = useState(0);
    const [postLoginUrl, setPostLoginUrl] = useState('');
    const [showUpdatedTerms, setShowUpdatedTerms] = useState(false);

    function incrementResendRequestCount() {
        setResendRequestCount(resendRequestCount + 1);
    }

    function handleError({ target, errors }: { target?: string; errors: ServerError[] }) {
        const [error] = errors;
        if (error) {
            if (error.param === 'cookie' && target) {
                if (onAuthSuccess) {
                    onAuthSuccess(target);
                } else {
                    window.location.assign(target);
                }
                return;
            }

            dispatch({ data: { error }, type: 'REQUEST_ERROR' });
            return;
        }

        throw new Error('Unhandled error');
    }

    function verifyToken(token: string) {
        dispatch({ type: 'REQUEST_SENT' });
        const url = new URL('/accounts/xhr/verify-token', window.location.origin);

        fetch(url.toString(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ challenge_token: token, csrf_token: getCsrfToken() }),
        })
            .then((response) => response.json().then((d) => ({ status: response.status, body: d })))
            .then((d) => {
                if (d.status === 200) {
                    dispatch({ type: 'REQUEST_SUCCESS' });
                    if (d.body.must_view_terms) {
                        setPostLoginUrl(d.body.target);
                        setShowUpdatedTerms(true);
                        return;
                    }

                    if (d.body.target) {
                        if (onAuthSuccess) {
                            onAuthSuccess(d.body.target);
                        } else {
                            window.location.assign(d.body.target);
                        }
                    }
                    return;
                }
                handleError(d.body);
            })
            .catch((error) => {
                dispatch({ type: 'GENERIC_ERROR' });
                Bugsnag.notify(error, (event) => {
                    event.context = 'verifying login token';
                });
            });
    }

    function requestNewToken() {
        dispatch({ type: 'RESEND_REQUEST_SENT' });
        const url = new URL('/accounts/xhr/resend-token', window.location.origin);

        fetch(url.toString(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ csrf_token: getCsrfToken() }),
        })
            .then((response) => response.json().then((d) => ({ status: response.status, body: d })))
            .then((d) => {
                if (d.status === 200) {
                    dispatch({ type: 'RESEND_REQUEST_SUCCESS' });
                    incrementResendRequestCount();
                    return;
                }
                handleError(d.body);
            })
            .catch((error) => {
                dispatch({
                    type: 'RESEND_REQUEST_ERROR',
                });
                Bugsnag.notify(error, (event) => {
                    event.context = 'resending login token';
                });
            });
    }

    function uiReducer(state: State, action: Action): State {
        switch (action.type) {
            case 'REQUEST_SENT':
                return {
                    ...state,
                    error: null,
                    ui: 'LOADING',
                };
            case 'RESEND_REQUEST_SENT':
                return {
                    ...state,
                    error: null,
                    ui: 'RESEND_LOADING',
                };
            case 'REQUEST_SUCCESS':
                return {
                    ...state,
                    error: null,
                    ui: 'SUCCESS',
                };
            case 'RESEND_REQUEST_SUCCESS':
                return {
                    ...state,
                    error: null,
                    ui: 'RESEND_SUCCESS',
                };
            case 'REQUEST_ERROR':
            case 'RESEND_REQUEST_ERROR':
            case 'GENERIC_ERROR':
                return {
                    ...state,
                    error: calculateErrorType(action.data?.error),
                    ui: 'ERROR',
                };
            default:
                return {
                    ...state,
                    error: null,
                    ui: 'IDLE',
                };
        }
    }

    return {
        uiState: state.ui,
        error: state.error,
        verifyToken: verifyToken,
        resendToken: requestNewToken,
        resendRequestCount: resendRequestCount,
        showUpdatedTerms: showUpdatedTerms,
        postLoginUrl: postLoginUrl,
    };
}
